import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  GetServiceCaller,
  PostFormServiceCaller,
} from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import SearchBar from "../../../../components/SearchBar";
import Pagination from "../../../../components/Pagination";
import ExportToExcel from "../../../../components/ExportToExcel";
import { FaSpinner } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Upload, FileText, X, Check } from "lucide-react";
import CustomDatePicker from "../../../../components/Custom-Datepicker";
import moment from "moment";
import { toast } from "react-toastify";

const Column = {
  FullName: { text: "Name", visible: true },
  MobileNo: { text: "Contact No.", align: "center", visible: true },
  CurrentExperienceInYears: {
    text: "Exp. (In Yrs)",
    align: "center",
    visible: true,
  },
  EmailID: { text: "Email", visible: true },
  SkillSet: { text: "Skills", visible: true },
  SubSkillSet: { text: "Sub Skills", visible: true },
  Genders: { text: "Gender", visible: false },
  DateOfBirth: { text: "DOB", visible: false, align: "center" },
  CurrentCompanyName: { text: "Company", visible: false },
  CurrentPosition: { text: "Designation", visible: false },
  CurrentLocation: { text: "Location", visible: false },
  Status: { text: "Status", visible: true },
  CurrentCTC: { text: "Current CTC", align: "right", visible: false },
  ExpectedCTC: { text: "Expected CTC", align: "right", visible: false },
  NoticePeriod: { text: "Notice Period", visible: false, align: "center" },
  Qualifications: { text: "Qualifications", visible: false },
  PreferredLocation: { text: "Preferred Location", visible: false },
};

const recordPerPage = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const ResumeParser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const excelName = useSelector(
    ({ layoutReducer }) => layoutReducer?.filterSlice?.navbarHeading
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [apiStatus, setApiStatus] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);
  const [columns, setColumns] = useState(Column);
  const [showDropdown, setShowDropdown] = useState(false);
  const [skillsOptions, setSKillsOptions] = useState([]);
  const [subSkillsOptions, setSubSKillsOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [formData, setFormData] = useState({
    Skill: null,
    SubSkill: null,
    fromDate: "",
    toDate: "",
  });

  useEffect(() => {
    dispatch(updateNavbarHeading("Resume Parser"));
    fetchResumeParserData();
  }, [dispatch]);

  const getColumnWidth = (columnKey) => {
    const widths = {
      FullName: "150px",
      MobileNo: "120px",
      CurrentExperienceInYears: "80px",
      EmailID: "200px",
      SkillSet: "150px",
      SubSkillSet: "150px",
      Genders: "100px",
      DateOfBirth: "120px",
      CurrentCompanyName: "200px",
      CurrentPosition: "150px",
      CurrentLocation: "150px",
      Status: "80px",
      CurrentCTC: "120px",
      ExpectedCTC: "120px",
      NoticePeriod: "120px",
      Qualifications: "200px",
      PreferredLocation: "150px",
    };
    return widths[columnKey] || "150px";
  };
  const filteredData = useMemo(() => {
    if (!searchTerm) return resumeData;
    return resumeData.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [resumeData, searchTerm]);
  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * selectedRecordsPerPage;
    const indexOfFirstItem = indexOfLastItem - selectedRecordsPerPage;
    return filteredData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredData, currentPage, selectedRecordsPerPage]);

  useEffect(() => {
    // Initialize Bootstrap tooltips
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [currentItems]);

  const fetchResumeParserData = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}ai-parsed-candidate-details?` +
          `skill=${params.skill || ""}&` +
          `subSkill=${params.subSkill || ""}&` +
          `from=${params.from || ""}&` +
          `to=${params.to || ""}`
      );
      setApiStatus(response?.status);

      if (response.status === 200) {
        const formattedData =
          response?.data?.data?.map((item) => ({
            ...item,
            DateOfBirth: item.DateOfBirth
              ? moment(item.DateOfBirth).format("DD-MMM-YYYY")
              : null,
            CurrentCTC: item.CurrentCTC
              ? Number(item.CurrentCTC).toFixed(2)
              : null,
            ExpectedCTC: item.ExpectedCTC
              ? Number(item.ExpectedCTC).toFixed(2)
              : null,
          })) || [];
        const ParseData = formattedData.reverse();
        setResumeData(ParseData);
      }
    } catch (error) {
      console.error("Error fetching Project:", error);
      setApiStatus(500);
    } finally {
      setLoading(false);
    }
  }, []);

  const totalItems = filteredData.length;
  const visibleColumns = useMemo(
    () => Object.keys(columns).filter((key) => columns[key].visible),
    [columns]
  );

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const toggleColumn = useCallback((columnKey) => {
    setColumns((prev) => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        visible: !prev[columnKey].visible,
      },
    }));
  }, []);

  const handleRecordsPerPage = useCallback((e) => {
    const newRecordsPerPage = Number(e.target.value);
    setSelectedRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  }, []);

  const prepareExcelData = useCallback(() => {
    return currentItems.map((item) => {
      const excelItem = {};
      visibleColumns.forEach((column) => {
        excelItem[columns[column].text] = item[column] || "-";
      });
      return excelItem;
    });
  }, [currentItems, visibleColumns, columns]);

  const handleEdit = (id) => {
    navigate(`/resume-parser/edit/${id}`);
  };

  const renderTableHeader = () => (
    <thead id="thcolor">
      <tr className="text-center">
        {visibleColumns.map((column) => (
          <th
            key={column}
            style={{
              width: getColumnWidth(column),
              overflow: "hidden",
              //   textAlign: columns[column].align || 'left'
            }}
          >
            {columns[column].text}
          </th>
        ))}
        <th style={{ width: "80px" }}>Action</th>
      </tr>
    </thead>
  );

  const renderTableBody = () => {
    if (loading) {
      return (
        <tr>
          <td
            colSpan={visibleColumns.length + 1}
            className="text-center"
            style={{ padding: "50px 0" }}
          >
            <div>
              <p>
                <FaSpinner className="spinner" />
                <br />
                Loading
              </p>
            </div>
          </td>
        </tr>
      );
    }

    if (!currentItems.length && apiStatus === 200) {
      return (
        <tr>
          <td
            colSpan={visibleColumns.length + 1}
            className="text-center text-muted"
            style={{ padding: "20px 0" }}
          >
            No Data Found
          </td>
        </tr>
      );
    }

    return currentItems.map((item, index) => (
      <tr key={index}>
        {visibleColumns.map((column) => (
          <td
            key={`${index}-${column}`}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: getColumnWidth(column),
              textAlign: columns[column].align || "left",
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={item[column] || "-"}
          >
            {item[column] || "-"}
          </td>
        ))}
        <td className="text-center" style={{ width: "80px" }}>
          <i
            className="bi bi-pencil-square"
            onClick={() => handleEdit(item.ID)}
            style={{ cursor: "pointer" }}
          ></i>
        </td>
      </tr>
    ));
  };

  const handleModel = () => {
    setShowModal(true);
  };

  // Handle file section
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    // Check if file type is allowed
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload only PDF, DOC, or DOCX files");
      return;
    }
    if (file && file.size <= 500 * 1024 * 1024) {
      setUploadedFile(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Skill subskill API
  useEffect(() => {
    fetchSkillSubSkillData();
  }, []);

  const fetchSkillSubSkillData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}parsed-candidate-filter`
      );
      setApiStatus(response?.status);
      if (response.status === 200) {
        // Skill List
        const skillOptions = response?.data?.skills?.map((option) => ({
          value: option.ID,
          label: option.Name,
        }));
        setSKillsOptions(skillOptions);
        // Sub Skill List
        const SubSkillOptions = response?.data?.subskills?.map((option) => ({
          value: option.ID,
          label: option.SubSkill,
        }));
        setSubSKillsOptions(SubSkillOptions);
      }
    } catch (error) {
      console.error("Error fetching Data:", error);
      setApiStatus(500);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e, selectedOption, fieldName) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedOption,
    }));
  };

  const handleSelectChange = (selectedOption, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedOption,
    }));
  };

  const handleGetData = () => {
    const params = {
      skill: formData.Skill?.value || "",
      subSkill: formData.SubSkill?.value || "",
      from: formData.fromDate
        ? moment(formData.fromDate).format("YYYY-MM-DD")
        : "",
      to: formData.toDate ? moment(formData.toDate).format("YYYY-MM-DD") : "",
    };

    fetchResumeParserData(params);
    setShowDropdown(false);
  };

  const handleReset = () => {
    setFormData({
      Skill: null,
      SubSkill: null,
      fromDate: "",
      toDate: "",
    });

    fetchResumeParserData({
      skill: "",
      subSkill: "",
      from: "",
      to: "",
    });

    setShowDropdown(false);
  };

  // const extractNumericExperience = (expString) => {
  //   if (!expString) return "";

  //   // Match the first number in the string (integer or decimal)
  //   const match = expString.match(/\d+(\.\d+)?/);
  //   return match ? match[0] : "";
  // };


  const handleSubmit = async () => {
  if (!uploadedFile) {
    toast.error("Please select a file first");
    return;
  }

  setIsLoading(true);

  try {
    const formData = new FormData();
    formData.append("file", uploadedFile);

    const response = await PostFormServiceCaller(
      "https://api.nicesoftwaresolutions.com/parse-resume",
      formData,
      "post",
      7000
    );

    if (response.status !== 200) {
      throw new Error("Upload failed");
    }

    const result = response?.data;
    if (result) {
      try {
        let genderValue = "";
        if (result.Gender) {
          const gender = result.Gender.toLowerCase().trim();
          if (gender === "male" || gender === "m") {
            genderValue = 148;
          } else if (gender === "female" || gender === "f") {
            genderValue = 149;
          }
        }
        const secondFormData = new FormData();

        secondFormData.append("FirstName", result.FirstName || "");
        secondFormData.append("LastName", result.LastName || "");
        secondFormData.append("FullName", result.FullName || "");
        secondFormData.append("EmailID", result.EmailID || "");
        secondFormData.append("MobileNo", result.MobileNo || "");
        secondFormData.append("MobileNo1", result.MobileNo1 || "");
        secondFormData.append(
          "DateOfBirth",
          result.DateOfBirth
            ? moment(result.DateOfBirth, [
                "Do MMM YYYY",
                "DD-MM-YYYY",
                "YYYY-MM-DD",
              ]).format("YYYY/MM/DD")
            : ""
        );
        secondFormData.append("Gender", genderValue);
        secondFormData.append(
          "CurrentLocation",
          result.CurrentLocation || ""
        );
        secondFormData.append(
          "CorrespondenceAddress",
          result.CorrespondenceAddress || ""
        );
        secondFormData.append(
          "PermanentAddress",
          result.PermanentAddress || ""
        );
        secondFormData.append(
          "PreferredLocation",
          result.PreferredLocation || ""
        );
        secondFormData.append(
          "CurrentCompanyName",
          result.CurrentCompanyName || ""
        );
        secondFormData.append("CurrentPosition", result.CurrentPosition || "");
        secondFormData.append(
          "CurrentExperienceInYears",
          result.CurrentExperienceInYears || ""
        );
        secondFormData.append(
          "CurrentRelevantExperienceInYears",
          result.CurrentRelevantExperienceInYears || ""
        );
        secondFormData.append("CurrentCTC", result.CurrentCTC || "");
        secondFormData.append("ExpectedCTC", result.ExpectedCTC || "");
        secondFormData.append("NoticePeriod", result.NoticePeriod || "");
        secondFormData.append("SkillSet", result.SkillSet || "");
        secondFormData.append("SubSkillSet", result.SubSkillSet || "");
        secondFormData.append("ProjectKnows", result.ProjectKnows || "");
        secondFormData.append(
          "ProjectTechnology",
          result.ProjectTechnology || ""
        );
        secondFormData.append(
          "TechnologyRemark",
          result.TechnologyRemark || ""
        );
        secondFormData.append("Qualifications", result.Qualifications || "");
        secondFormData.append(
          "NoticePeriodExtractedText",
          result.NoticePeriodExtractedText || ""
        );
        secondFormData.append("ProjectWork", result.ProjectWork || "");
        secondFormData.append(
          "IntroduceYourself",
          result.IntroduceYourself || ""
        );
        secondFormData.append("createddate", new Date().toISOString());
        secondFormData.append(
          "CurrentJobDescription",
          result.CurrentJobDescription || ""
        );

        // Append the original resume file to the second formData
        secondFormData.append("ResumePath", uploadedFile);
        const aiResponse = await PostFormServiceCaller(
          `${API_URL}ai-parsed-candidate-details`,
          secondFormData,
          "post",
          7000
        );

        setApiResponse(aiResponse.data);
        if (aiResponse?.data?.status) {
          toast.success(
            aiResponse.data.message || "Resume Uploaded successfully!"
          );
        } else {
          console.error(aiResponse?.data?.message || "Something went wrong!");
        }
        fetchResumeParserData();
        setShowModal(false);
      } catch (aiError) {
        console.error("Error in second API call:", aiError);

        if (aiError.message?.toLowerCase().includes("network error")) {
          toast.error("Please Connect to VPN", {autoClose: 7000});
        } else {
          console.error("Error in processing resume details.");
        }
      }
    }

    // Reset form
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  } catch (error) {
    console.error("Error uploading file:", error);

    if (error.message?.toLowerCase().includes("network error")) {
      toast.error("Please Connect to VPN");
    } else {
      toast.error("Error uploading file. Please try again.");
    }
  } finally {
    setIsLoading(false);
  }
};

  const handleCancel = () => {
    setShowModal(false);
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="container-fluid" id="skill_section">
      <div className="rounded bg-white" id="skill-table-area">
        <div className="d-flex justify-content-between">
          <div className="me-1 d-flex">
            <div className="me-2">
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
            >
              <div className="btn-group me-2">
                <button
                  type="button"
                  className="dropdown-toggle skill-button"
                  data-bs-toggle="dropdown"
                  onClick={() => setShowDropdown(!showDropdown)}
                  aria-expanded="false"
                  id="excel-btn"
                >
                  <i className="bi bi-funnel-fill"></i>
                </button>
                <ul
                  className={`dropdown-menu p-2 project-status-filter ${
                    showDropdown ? "show" : ""
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="row">
                    <div className="col-md-4">
                      <label className="form-label">Skill</label>
                      <Select
                        options={skillsOptions}
                        onChange={(selectedOption) =>
                          handleSelectChange(selectedOption, "Skill")
                        }
                        value={formData.Skill}
                        name="Skill"
                      />
                    </div>
                           {/* <div className="col-md-4">
                      <label className="form-label">Sub Skill</label>
                      <Select
                        options={subSkillsOptions}
                        onChange={(selectedOption) =>
                          handleSelectChange(selectedOption, "SubSkill")
                        }
                        value={formData.SubSkill}
                        name="SubSkill"
                      />
                    </div> */}
                    <div className="col-md-4">
                      <label className="form-label">From Date</label>
                      <CustomDatePicker
                        name="fromDate"
                        className="form-control"
                        placeholderText="DD-MMM-YYYY"
                        handleChange={(e) => handleChange(e)}
                        selected={formData.fromDate}
                        value={formData.fromDate}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">To Date</label>
                      <CustomDatePicker
                        name="toDate"
                        className="form-control"
                        placeholderText="DD-MMM-YYYY"
                        handleChange={(e) => handleChange(e)}
                        value={formData.toDate}
                        selected={formData.toDate}
                      />
                    </div>
                  </div>

                  <div className="row mt-2">
                    <div className="text-end">
                      <button className="custom-btn mx-2" onClick={handleReset}>
                        Reset
                      </button>
                      <button className="custom-btn" onClick={handleGetData}>
                        Get Data
                      </button>
                    </div>
                  </div>
                </ul>
              </div>
            </OverlayTrigger>
          </div>

          <div className="d-flex justify-content-end">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Export to Excel</Tooltip>}
            >
              <div className="me-1">
                <ExportToExcel
                  data={prepareExcelData()}
                  filename={`${excelName}.csv`}
                />
              </div>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-top">Column Chooser</Tooltip>}
            >
              <div className="btn-group me-2">
                <button
                  type="button"
                  className="dropdown-toggle skill-button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  id="excel-btn"
                >
                  <i className="bi bi-list"></i>
                </button>
                <ul className="dropdown-menu p-3">
                  <div className="row">
                    <Form.Group>
                      {Object.keys(columns).map((columnKey) => (
                        <Form.Check
                          key={columnKey}
                          type="checkbox"
                          id={`checkbox-${columnKey}`}
                          label={columns[columnKey].text}
                          checked={columns[columnKey].visible}
                          onChange={() => toggleColumn(columnKey)}
                        />
                      ))}
                    </Form.Group>
                  </div>
                </ul>
              </div>
            </OverlayTrigger>

            <button id="Add-btn" onClick={handleModel}>
              Upload Resume
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <Table bordered striped hover style={{ tableLayout: "fixed" }}>
            {renderTableHeader()}
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </div>

        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between mb-3">
          <div className="d-flex justify-content-between px-2">
            <span style={{ width: "150px" }}>
              Showing {(currentPage - 1) * selectedRecordsPerPage + 1} -{" "}
              {Math.min(currentPage * selectedRecordsPerPage, totalItems)} of{" "}
              {totalItems}
            </span>

            <div style={{ width: "80px" }}>
              <select
                value={selectedRecordsPerPage}
                onChange={handleRecordsPerPage}
                className="form-select form-select-sm ms-2"
              >
                {recordPerPage.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                {totalItems > 100 && (
                  <option value={totalItems}>All ({totalItems})</option>
                )}
              </select>
            </div>
          </div>

          <div className="d-flex justify-content-center justify-content-sm-end mt-2 mt-sm-0 w-100">
            <Pagination
              itemsPerPage={selectedRecordsPerPage}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal"
          tabIndex="-1"
          role="dialog"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1050,
          }}
        >
          <div
            className="modal-dialog modal-sm"
            role="document"
            style={{
              width: "300px",
              margin: 0,
            }}
          >
            <div className="modal-content">
              <div
                className="modal-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #f0f0f0",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#27568B",
                    margin: "0",
                  }}
                >
                  Upload Resume
                </p>

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
              <div
                className="modal d-flex align-items-center justify-content-center"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 1050,
                  display: "flex !important",
                }}
              >
                {/* Modal Dialog */}
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{ maxWidth: "450px" }}
                >
                  <div
                    className="modal-content shadow-lg"
                    style={{
                      borderRadius: "16px",
                      border: "none",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div className="modal-header border-0 pb-2">
                      <h5 className="modal-title fw-semibold text-dark">
                        Upload Resume
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCancel}
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: "1.2rem",
                          opacity: 0.6,
                        }}
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="modal-body px-4 pb-4">
                      {/* Upload Area */}
                      <div className="mb-4">
                        {!uploadedFile ? (
                          <div
                            className={`border border-2 border-dashed rounded p-5 text-center position-relative ${
                              isDragging
                                ? "border-primary bg-light"
                                : "border-secondary"
                            }`}
                            style={{
                              borderRadius: "12px",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              backgroundColor: isDragging
                                ? "#f8f9fa"
                                : "#fafafa",
                            }}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onMouseEnter={(e) => {
                              if (!isDragging) {
                                e.target.style.backgroundColor = "#f8f9fa";
                                e.target.style.borderColor = "#6c757d";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDragging) {
                                e.target.style.backgroundColor = "#fafafa";
                                e.target.style.borderColor = "#dee2e6";
                              }
                            }}
                          >
                            {/* Upload Icon */}
                            <div className="mb-3">
                              <div
                                className="d-inline-flex align-items-center justify-content-center"
                                style={{
                                  width: "64px",
                                  height: "64px",
                                  backgroundColor: "#e9ecef",
                                  borderRadius: "16px",
                                }}
                              >
                                <Upload
                                  size={32}
                                  style={{ color: "#6f42c1" }}
                                />
                              </div>
                            </div>

                            {/* Upload Text */}
                            <div>
                              <p className="mb-2 fw-medium text-dark">
                                Drag and drop or{" "}
                                <button
                                  type="button"
                                  className="btn p-0 text-primary fw-semibold text-decoration-underline"
                                  style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "inherit",
                                  }}
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  click to upload
                                </button>
                              </p>
                              <p className="text-muted small mb-0">
                                Maximum file size 5 MB
                              </p>
                              <p className="small mb-0 text-warning">
                                Please upload a PDF, DOCX, or DOC file.
                              </p>
                            </div>

                            {/* Hidden File Input */}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleFileSelect}
                              style={{ display: "none" }}
                            />
                          </div>
                        ) : (
                          /* Uploaded File Display */
                          <div
                            className="border rounded p-3"
                            style={{
                              borderColor: "#198754 !important",
                              backgroundColor: "#d1e7dd",
                              borderRadius: "12px",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div
                                  className="d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "#198754",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <FileText size={20} color="white" />
                                </div>
                                <div>
                                  <p
                                    className="mb-1 fw-medium text-dark"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {uploadedFile.name.length > 30
                                      ? `${uploadedFile.name.substring(
                                          0,
                                          30
                                        )}...`
                                      : uploadedFile.name}
                                  </p>
                                  <p className="text-muted small mb-0">
                                    {(uploadedFile.size / 1024 / 1024).toFixed(
                                      2
                                    )}{" "}
                                    MB
                                  </p>
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <div
                                  className="d-flex align-items-center justify-content-center me-2"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    backgroundColor: "#198754",
                                    borderRadius: "50%",
                                  }}
                                >
                                  <Check size={14} color="white" />
                                </div>
                                <button
                                  type="button"
                                  className="btn p-1"
                                  onClick={removeFile}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#dc3545",
                                    borderRadius: "50%",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = "#f8d7da";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="custom-btn"
                          onClick={handleSubmit}
                        >
                          {isLoading ? (
                            <>
                              <FaSpinner class="sm-spinner" />
                            </>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeParser;
